import { CONTROL_SLIDER_SIZES } from '../controls/controlTokens';

/**
 * Filter configuration definitions for the UnifiedFilterBar component.
 * Maps filter types to their corresponding components and state management patterns.
 */

/**
 * Filter type definitions
 * Each type maps to a component and defines its state management behavior
 */
export const FILTER_TYPES = {
  // Category filter with hierarchical parent/child toggle behavior
  category: {
    component: 'Category',
    stateKey: 'selectedCategory',
    cacheKey: 'selectedCategory',
    defaultWidth: 170,
  },

  // Flat category filter without hierarchical structure
  categoryFlat: {
    component: 'CategoryFlat',
    stateKey: 'checked',
    cacheKey: null,
    defaultWidth: 250,
  },

  // Index category filter for benchmark pages
  indexCategory: {
    component: 'IndexCategory',
    stateKey: 'checked',
    cacheKey: null,
    defaultWidth: 308,
  },

  // Ticker multi-select with favorites support
  ticker: {
    component: 'TickerMultiSelect',
    stateKey: 'selectedTickers',
    cacheKey: null,
    defaultWidth: 180,
  },

  // Dashboard/table column fields selector
  fields: {
    component: 'DashboardFields',
    stateKey: 'selectedColumns',
    cacheKey: 'selectedColumns',
    defaultWidth: 250,
  },

  // Slider for time range selection
  slider: {
    component: 'Slider',
    stateKey: 'sliderValue',
    cacheKey: null,
    defaultWidth: CONTROL_SLIDER_SIZES.width,
  },

  // Toggle button group for view switching
  toggle: {
    component: 'FilterToggle',
    stateKey: 'toggleValue',
    cacheKey: null,
    defaultWidth: 125,
  },

  // Compact shared control toggle used in workspace/filter-bar surfaces
  controlToggle: {
    component: 'ControlToggleGroup',
    stateKey: 'toggleValue',
    cacheKey: null,
    defaultWidth: 'auto',
  },

  // Grouped multi-select for compact cross-dimension filtering
  groupedMultiSelect: {
    component: 'ControlGroupedMultiSelect',
    stateKey: 'groupedValues',
    cacheKey: null,
    defaultWidth: 210,
  },

  // Numeric input filter for threshold/min values
  number: {
    component: 'NumberInput',
    stateKey: 'numberValue',
    cacheKey: null,
    defaultWidth: 100,
  },

  // Status filter (Public/Private) using table fields style
  status: {
    component: 'Status',
    stateKey: 'status',
    cacheKey: null,
    defaultWidth: 140,
  },

  // Table fields filter (simpler than dashboard fields)
  tableFields: {
    component: 'TableFields',
    stateKey: 'selectedColumns',
    cacheKey: null,
    defaultWidth: 200,
  },

  // Switch toggle for boolean state (simple)
  switch: {
    component: 'Switch',
    stateKey: null,
    cacheKey: null,
    defaultWidth: 'auto',
  },

  // Icon switch toggle - styled switch with optional icon (valuation, dashboard compare)
  iconSwitch: {
    component: 'IconSwitch',
    stateKey: null,
    cacheKey: null,
    defaultWidth: 125,
  },

  // Simple single-select dropdown matching DashboardFields style
  simpleSelect: {
    component: 'SimpleSelect',
    stateKey: 'selectedValue',
    cacheKey: null,
    defaultWidth: 170,
  },

  // Asset class filter with hierarchical parent/child toggle behavior
  assetClassFilter: {
    component: 'AssetClassFilter',
    stateKey: 'assetClassSelection',
    cacheKey: null,
    defaultWidth: 280,
  },

  // Custom component - allows passing any component
  custom: {
    component: null,
    stateKey: null,
    cacheKey: null,
    defaultWidth: 'auto',
  },

  // Button toggle - Annual/Quarterly selection
  timeFrame: {
    component: 'ButtonTimeFrame',
    stateKey: 'alignment',
    cacheKey: null,
    defaultWidth: 118,
  },

  // Time period toggle - 1M, 3M, YTD, 1Y, 3Y, 5Y, 10Y
  timeFramePeriod: {
    component: 'ButtonTimeFramePeriod',
    stateKey: 'alignment',
    cacheKey: null,
    defaultWidth: 280,
  },

  // Format values toggle - Millions/Billions
  formatValues: {
    component: 'ButtonFormatValues',
    stateKey: 'alignmentScale',
    cacheKey: null,
    defaultWidth: 110,
  },

  // Calculation type selector - RAW, % Growth, $ Change
  calculationType: {
    component: 'ButtonCalculationType',
    stateKey: 'selectedCalculation',
    cacheKey: null,
    defaultWidth: 120,
  },

  // Enhanced slider with period presets
  enhancedSlider: {
    component: 'EnhancedSlider',
    stateKey: 'sliderValue',
    cacheKey: null,
    defaultWidth: CONTROL_SLIDER_SIZES.width,
  },
};

/**
 * Default filter configurations by page type
 * Used when a page doesn't specify its own filter config
 */
export const PAGE_FILTER_PRESETS = {
  compsheet: [
    { id: 'category', type: 'category' },
    { id: 'ticker', type: 'ticker' },
    { id: 'columns', type: 'fields', width: 250 },
  ],
  dashboard: [
    { id: 'category', type: 'category' },
    { id: 'ticker', type: 'ticker' },
    { id: 'columns', type: 'fields', width: 250 },
  ],
  valuation: [
    { id: 'category', type: 'category' },
    { id: 'columns', type: 'fields', width: 250 },
  ],
  benchmarks: [
    { id: 'indexCategory', type: 'indexCategory' },
  ],
};

/**
 * Get filter type definition
 * @param {string} type - Filter type key
 * @returns {object} Filter type definition
 */
export const getFilterType = (type) => {
  return FILTER_TYPES[type] || FILTER_TYPES.custom;
};

/**
 * Get default width for a filter type
 * @param {string} type - Filter type key
 * @param {number} customWidth - Custom width override
 * @returns {number|string} Width value
 */
export const getFilterWidth = (type, customWidth) => {
  if (customWidth !== undefined) return customWidth;
  const filterType = getFilterType(type);
  return filterType.defaultWidth;
};

/**
 * Get cache key for filter state
 * @param {string} type - Filter type key
 * @param {string} prefix - Optional prefix for namespacing
 * @returns {string|null} Cache key or null if not cacheable
 */
export const getFilterCacheKey = (type, prefix = '') => {
  const filterType = getFilterType(type);
  if (!filterType.cacheKey) return null;
  return prefix ? `${prefix}_${filterType.cacheKey}` : filterType.cacheKey;
};

/**
 * Validate filter configuration
 * @param {object} config - Filter configuration object
 * @returns {boolean} Whether config is valid
 */
export const validateFilterConfig = (config) => {
  if (!config || typeof config !== 'object') return false;
  if (!config.id || typeof config.id !== 'string') return false;
  if (!config.type || !FILTER_TYPES[config.type]) return false;
  return true;
};

/**
 * Normalize filter configuration array
 * Ensures all configs have required fields with defaults
 * @param {Array} filters - Array of filter configs
 * @returns {Array} Normalized filter configs
 */
export const normalizeFilterConfigs = (filters) => {
  if (!Array.isArray(filters)) return [];

  return filters
    .filter(validateFilterConfig)
    .map((config) => {
      const filterType = getFilterType(config.type);
      return {
        ...config,
        width: config.width ?? filterType.defaultWidth,
        props: config.props || {},
      };
    });
};
